import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivityMetricsV2ChunkDiagnostic, ActivityMetricsV2Diagnostics, ActivityMetricsV2ExportResponse } from '../../../../shared/models/activity-metrics-v2.model';

interface FixedPointRuleSummaryRow {
  key: string;
  label: string;
  primaryRule: string;
  approvedFallback: string;
  windowBehavior: string;
}

@Component({
  selector: 'app-agent-performance-activity-metrics-diagnostics-tail-widget',
  templateUrl: './agent-performance-activity-metrics-diagnostics-tail-widget.component.html',
  styleUrls: ['./agent-performance-activity-metrics-diagnostics-tail-widget.component.scss']
})
export class AgentPerformanceActivityMetricsDiagnosticsTailWidgetComponent {
  @Input() collapsedSections: Record<string, boolean> = {};
  @Input() diagnostics: ActivityMetricsV2Diagnostics | null = null;
  @Input() diagnosticsSourceRows = 0;
  @Input() diagnosticsWarnings: string[] = [];
  @Input() diagnosticsChunkRows: ActivityMetricsV2ChunkDiagnostic[] = [];
  @Input() exportPreview: ActivityMetricsV2ExportResponse | null = null;
  @Output() sectionToggled = new EventEmitter<string>();

  readonly fixedPointRuleSummary: FixedPointRuleSummaryRow[] = [
    {
      key: 'activity_admission',
      label: 'Activity Admission',
      primaryRule: 'Jobs and subjects are admitted by in-window activity across the selected sources.',
      approvedFallback: 'No metric inference. Admission only determines which subject-stages enter evaluation.',
      windowBehavior: 'Always in-window. This is the report gate, not a metric endpoint.'
    },
    {
      key: 'job_loaded_at',
      label: 'Job Loaded',
      primaryRule: 'Base stage uses jobs.created_date. Spawned or reference-created later stages reset to subjects.created_date.',
      approvedFallback: 'Stage created date only. No later activity may replace the loaded anchor.',
      windowBehavior: 'May sit outside the selected activity window.'
    },
    {
      key: 'job_opened_at',
      label: 'Job Opened',
      primaryRule: 'Qualifying historical open/create log for the admitted subject-stage.',
      approvedFallback: 'Earliest staff-recorded action for the subject-stage, then earliest admitted non-attempt activity, then the loaded anchor as the last deterministic fallback.',
      windowBehavior: 'May resolve from historical logs before the selected window.'
    },
    {
      key: 'job_instructions_sent_at',
      label: 'Instructions Sent To Agent',
      primaryRule: 'Earliest qualifying successful system_log.log_type_id = 61 Instruct Agent Communication event for the admitted job / subject-stage context.',
      approvedFallback: 'No fallback. Missing when no qualifying instruction-success log exists. Future log_type_id = 75 support is defined but not active.',
      windowBehavior: 'May resolve from historical logs before the selected window.'
    },
    {
      key: 'first_actual_attempt',
      label: 'First Actual Attempt',
      primaryRule: 'First admitted subject_services attempt event with a usable attempt/service timestamp.',
      approvedFallback: 'No staff-entry timestamp fallback. subject_services.created_date is not used as actual attendance truth.',
      windowBehavior: 'Resolved from the admitted attempt sequence, not generic logs.'
    },
    {
      key: 'second_third_attempts',
      label: 'Second / Third Actual Attempt',
      primaryRule: 'Second and third admitted subject_services attempt events in the same subject-stage sequence.',
      approvedFallback: 'No synthetic attempt creation from notes, logs, files, or communications.',
      windowBehavior: 'Resolved from the admitted attempt sequence for the report scope; if that sequence is empty, the helper reparses subject_services rows for the subject-stage only.'
    },
    {
      key: 'job_closed_at',
      label: 'Job Closed',
      primaryRule: 'Explicit close evidence only for the admitted job / subject-stage context.',
      approvedFallback: 'No last-touch substitution at this point. Missing when no explicit close evidence exists.',
      windowBehavior: 'May resolve from contextual close evidence outside the selected activity slice.'
    },
    {
      key: 'job_last_touch_at',
      label: 'Job Last Touch',
      primaryRule: 'Latest in-range normalized activity event for the admitted job / subject-stage context.',
      approvedFallback: 'No synthetic close inference. Missing when no in-range activity exists.',
      windowBehavior: 'Always tied to the selected activity window.'
    },
    {
      key: 'closed_or_last_touch',
      label: 'Closed / Last Touch Compatibility',
      primaryRule: 'Prefer explicit job.closed_at when present.',
      approvedFallback: 'Use job.last_touch_at only when explicit close evidence does not exist.',
      windowBehavior: 'Exposes whether the metric ended on a true close point or a last-touch fallback.'
    }
  ];

  isSectionCollapsed(sectionKey: string): boolean {
    return !!this.collapsedSections[sectionKey];
  }

  toggleSection(sectionKey: string): void {
    this.sectionToggled.emit(sectionKey);
  }

  get diagnosticsChunking() {
    return this.diagnostics?.chunking ?? null;
  }

  formatMilliseconds(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '-';
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }

    return `${Math.round(value)}ms`;
  }

  formatStageLabel(stageKey: string | null | undefined): string {
    if (!stageKey) {
      return '-';
    }

    return stageKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatReasonLabel(reasonKey: string | null | undefined): string {
    if (!reasonKey) {
      return '-';
    }

    return reasonKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }
}
